import React, { useEffect, useId } from "react";
import { createPortal } from "react-dom";
import "./reusable-modal.css";

const ReusableModal = ({
  open,
  isOpen,
  title,
  onClose,
  children,
  width = "760px",
  closeOnBackdrop = true,
  className = "",
  style = {},
}) => {
  const modalOpen = open ?? isOpen;
  const titleId = useId();

  useEffect(() => {
    if (!modalOpen) return;

    const body = document.body;
    const html = document.documentElement;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const vv = window.visualViewport;

    let rafId = 0;
    const timeoutIds = [];

    const applyModalViewport = () => {
      const viewportHeight = vv?.height || window.innerHeight;
      const viewportWidth = vv?.width || window.innerWidth;
      const viewportTop = vv?.offsetTop || 0;
      const viewportLeft = vv?.offsetLeft || 0;

      html.style.setProperty("--rm-vv-height", `${viewportHeight}px`);
      html.style.setProperty("--rm-vv-width", `${viewportWidth}px`);
      html.style.setProperty("--rm-vv-top", `${viewportTop}px`);
      html.style.setProperty("--rm-vv-left", `${viewportLeft}px`);
    };

    const syncModalViewport = () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(applyModalViewport);
    };

    const syncModalViewportAfterKeyboard = () => {
      syncModalViewport();
      timeoutIds.push(window.setTimeout(syncModalViewport, 80));
      timeoutIds.push(window.setTimeout(syncModalViewport, 250));
    };

    applyModalViewport();

    const prevBody = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
      touchAction: body.style.touchAction,
    };

    const prevHtmlOverflow = html.style.overflow;

    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    body.style.touchAction = prevBody.touchAction;
    html.style.overflow = "hidden";

    window.addEventListener("resize", syncModalViewport);
    window.addEventListener("orientationchange", syncModalViewportAfterKeyboard);
    window.addEventListener("focusin", syncModalViewportAfterKeyboard);
    window.addEventListener("focusout", syncModalViewportAfterKeyboard);

    vv?.addEventListener("resize", syncModalViewport);
    vv?.addEventListener("scroll", syncModalViewport);

    return () => {
      window.removeEventListener("resize", syncModalViewport);
      window.removeEventListener("orientationchange", syncModalViewportAfterKeyboard);
      window.removeEventListener("focusin", syncModalViewportAfterKeyboard);
      window.removeEventListener("focusout", syncModalViewportAfterKeyboard);

      vv?.removeEventListener("resize", syncModalViewport);
      vv?.removeEventListener("scroll", syncModalViewport);

      if (rafId) window.cancelAnimationFrame(rafId);
      timeoutIds.forEach((id) => window.clearTimeout(id));

      html.style.removeProperty("--rm-vv-height");
      html.style.removeProperty("--rm-vv-width");
      html.style.removeProperty("--rm-vv-top");
      html.style.removeProperty("--rm-vv-left");

      body.style.position = prevBody.position;
      body.style.top = prevBody.top;
      body.style.left = prevBody.left;
      body.style.right = prevBody.right;
      body.style.width = prevBody.width;
      body.style.overflow = prevBody.overflow;
      body.style.touchAction = prevBody.touchAction;
      html.style.overflow = prevHtmlOverflow;

      window.scrollTo(0, scrollY);
    };
  }, [modalOpen]);

  useEffect(() => {
    if (!modalOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [modalOpen, onClose]);

  if (!modalOpen) return null;

  const modalContent = (
    <div
      className="rm-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      aria-label={!title ? "Modal" : undefined}
    >
      <div
        className="rm-backdrop"
        onClick={() => {
          if (closeOnBackdrop) onClose?.();
        }}
      />

      <div
        className={`rm-card ${className}`}
        style={{ "--rm-max-width": width, ...style }}
      >
        <div className="rm-head">
          <h3 className="rm-title" id={titleId}>
            {title}
          </h3>

          <button
            type="button"
            className="rm-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="rm-body">{children}</div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ReusableModal;